// src/app/features/markets/marketApi.js
import http from "../../api/http.js";

// ✅ Helper to get backend WebSocket URL
function getBackendWsUrl(path) {
  const apiUrl = import.meta.env.VITE_API_URL || "https://worldcoins-4odu.onrender.com/api";
  const baseUrl = apiUrl.replace(/\/api$/, '');
  const url = new URL(baseUrl);
  const protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${url.host}${path}`;
}

export async function getMarketPrices() {
  try {
    const res = await http.get("/market/prices");
    return res.data;
  } catch (error) {
    console.error("Error fetching market prices:", error);
    // Return fallback data instead of throwing
    return getFallbackPrices();
  }
}

export async function getMarketPrice(symbol) {
  try {
    const res = await http.get(`/market/price/${encodeURIComponent(symbol)}`);
    return res.data;
  } catch (error) {
    console.error(`Error fetching price for ${symbol}:`, error);
    
    // Fallback to Binance directly
    try {
      const binancePrice = await fetchBinancePrice(symbol);
      return binancePrice;
    } catch (e) {
      // Last resort: return mock data
      return { symbol, price: getFallbackPrice(symbol), source: "fallback" };
    }
  }
}

// ✅ Direct Binance API call (no backend needed)
async function fetchBinancePrice(symbol) {
  const url = `https://api.binance.com/api/v3/ticker/price?symbol=${encodeURIComponent(symbol)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Binance price failed (${res.status})`);
  const json = await res.json();
  return { symbol, price: Number(json.price), source: "binance" };
}

async function fetchBinance24h(symbol) {
  const url = `https://api.binance.com/api/v3/ticker/24hr?symbol=${encodeURIComponent(symbol)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Binance 24hr failed (${res.status})`);
  const json = await res.json();
  return {
    symbol,
    price: Number(json.lastPrice),
    changePercent: Number(json.priceChangePercent),
    high: Number(json.highPrice),
    low: Number(json.lowPrice),
    volume: Number(json.volume),
    source: "binance"
  };
}

// ✅ Fallback prices
function getFallbackPrice(symbol) {
  const prices = {
    'BTCUSDT': 65000,
    'ETHUSDT': 3500,
    'SOLUSDT': 180,
    'BNBUSDT': 600,
    'XRPUSDT': 0.60,
    'ADAUSDT': 0.45,
    'DOGEUSDT': 0.15,
    'EURUSDT': 1.09,
    'GBPUSDT': 1.27,
    'AUDUSDT': 0.66,
    'JPYUSDT': 0.0067,
    'CADUSDT': 0.74,
    'CHFUSDT': 1.13,
    'PAXGUSDT': 2045,
  };
  return prices[symbol] || 100;
}

function getFallbackPrices() {
  const symbols = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT', 'XRPUSDT', 'ADAUSDT', 'DOGEUSDT'];
  const prices = {};
  symbols.forEach(s => {
    prices[s] = getFallbackPrice(s);
  });
  return prices;
}

// ✅ Updated getMarketTicker with fallback
export async function getMarketTicker() {
  try {
    const res = await http.get("/market/ticker");
    return res.data;
  } catch (error) {
    console.error("Error fetching market ticker from backend:", error);
    
    // Fallback: fetch directly from Binance
    try {
      const symbols = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT'];
      const results = await Promise.allSettled(symbols.map(s => fetchBinance24h(s)));
      
      const tickerData = results
        .filter(r => r.status === 'fulfilled')
        .map(r => r.value);
      
      return { data: tickerData, source: "binance" };
    } catch (e) {
      // Ultimate fallback
      return getFallbackTicker();
    }
  }
}

function getFallbackTicker() {
  const symbols = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT'];
  return {
    data: symbols.map(s => ({
      symbol: s,
      price: getFallbackPrice(s),
      changePercent: 0,
      high: getFallbackPrice(s) * 1.02,
      low: getFallbackPrice(s) * 0.98,
      volume: 0,
      source: "fallback"
    })),
    source: "fallback"
  };
}

export async function getPrice(symbol) {
  return getMarketPrice(symbol);
}

export async function getMarketHistory(symbol, params = {}) {
  try {
    const res = await http.get(`/market/history/${encodeURIComponent(symbol)}`, { params });
    return res.data;
  } catch (error) {
    console.error(`Error fetching history for ${symbol}:`, error);
    // Fallback to Binance klines
    try {
      const interval = params.interval || '1h';
      const limit = params.limit || 100;
      return await fetchBinanceKlines(symbol, interval, limit);
    } catch (e) {
      return generateMockHistory(symbol, limit || 100);
    }
  }
}

async function fetchBinanceKlines(symbol, interval, limit) {
  const url = `https://api.binance.com/api/v3/klines?symbol=${encodeURIComponent(symbol)}&interval=${encodeURIComponent(interval)}&limit=${Number(limit || 100)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Binance klines failed (${res.status})`);
  const rows = await res.json();
  return (Array.isArray(rows) ? rows : []).map((k) => ({
    time: Number(k[0]),
    open: Number(k[1]),
    high: Number(k[2]),
    low: Number(k[3]),
    close: Number(k[4]),
    volume: Number(k[5]),
  }));
}

function generateMockHistory(symbol, limit) {
  const basePrice = getFallbackPrice(symbol) || 100;
  const data = [];
  let price = basePrice;
  for (let i = 0; i < limit; i++) {
    const change = (Math.random() - 0.5) * 0.02 * price;
    price = Math.max(price + change, 0.01);
    data.push({
      time: Date.now() - (limit - i) * 60000,
      open: price * (1 - Math.random() * 0.005),
      high: price * (1 + Math.random() * 0.01),
      low: price * (1 - Math.random() * 0.01),
      close: price,
      volume: Math.random() * 1000,
    });
  }
  return data;
}

export async function getMarketOrderbook(symbol) {
  try {
    const res = await http.get(`/market/orderbook/${encodeURIComponent(symbol)}`);
    return res.data;
  } catch (error) {
    console.error(`Error fetching orderbook for ${symbol}:`, error);
    return { bids: [], asks: [] };
  }
}

export async function getMultiplePrices(symbols) {
  try {
    const res = await http.get("/market/prices/batch", {
      params: { symbols: symbols.join(',') }
    });
    return res.data;
  } catch (error) {
    console.error("Error fetching multiple prices:", error);
    
    if (error.response?.status === 404) {
      console.log("Batch endpoint not available, fetching prices individually");
      const promises = symbols.map(symbol => getMarketPrice(symbol).catch(e => ({
        symbol,
        price: null,
        error: e.message
      })));
      
      const results = await Promise.all(promises);
      return results.reduce((acc, result) => {
        acc[result.symbol] = result;
        return acc;
      }, {});
    }
    
    throw new Error(`Failed to fetch multiple prices: ${error.message}`);
  }
}

// ✅ SSE function
export function createPriceStream(symbols) {
  if (!window.EventSource) {
    throw new Error("EventSource is not supported in this browser");
  }
  
  const apiUrl = import.meta.env.VITE_API_URL || "https://worldcoins-4odu.onrender.com/api";
  const streamUrl = `${apiUrl}/market/stream/prices?symbols=${encodeURIComponent(symbols.join(','))}`;
  
  console.log("📡 Connecting to SSE:", streamUrl);
  
  const eventSource = new EventSource(streamUrl);
  
  return {
    eventSource,
    onMessage: (callback) => {
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          callback(data);
        } catch (error) {
          console.error("Error parsing SSE data:", error);
        }
      };
    },
    onError: (callback) => {
      eventSource.onerror = (error) => {
        console.error("📡 SSE error:", error);
        callback(error);
      };
    },
    close: () => {
      eventSource.close();
    }
  };
}

// ✅ WebSocket function
export function createWebSocketConnection(symbols) {
  const wsUrl = getBackendWsUrl("/api/market/ws/prices");
  
  console.log("🔌 Connecting to WebSocket:", wsUrl);
  
  const ws = new WebSocket(wsUrl);
  
  ws.onopen = () => {
    console.log("✅ WebSocket connected");
    ws.send(JSON.stringify({
      type: 'subscribe',
      symbols: symbols
    }));
  };
  
  return {
    ws,
    onMessage: (callback) => {
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          callback(data);
        } catch (error) {
          console.error("Error parsing WebSocket data:", error);
        }
      };
    },
    onError: (callback) => {
      ws.onerror = (error) => {
        console.error("❌ WebSocket error:", error);
        callback(error);
      };
    },
    onClose: (callback) => {
      ws.onclose = (event) => {
        console.log("🔌 WebSocket closed:", event.code, event.reason);
        callback?.(event);
      };
    },
    send: (data) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(data));
      } else {
        console.warn("WebSocket not open, current state:", ws.readyState);
      }
    },
    close: () => {
      ws.close();
    }
  };
}

export function normalizeSymbol(symbol) {
  const mapping = {
    'BTCUSDT': 'BTCUSDT',
    'ETHUSDT': 'ETHUSDT',
    'SOLUSDT': 'SOLUSDT',
    'BNBUSDT': 'BNBUSDT',
    'XRPUSDT': 'XRPUSDT',
    'ADAUSDT': 'ADAUSDT',
    'DOGEUSDT': 'DOGEUSDT',
    'EURUSDT': 'EURUSDT',
    'GBPUSDT': 'GBPUSDT',
    'AUDUSDT': 'AUDUSDT',
    'JPYUSDT': 'JPYUSDT',
    'CADUSDT': 'CADUSDT',
    'CHFUSDT': 'CHFUSDT',
    'PAXGUSDT': 'PAXGUSDT',
    'BTC/USDT': 'BTCUSDT',
    'ETH/USDT': 'ETHUSDT',
    'SOL/USDT': 'SOLUSDT',
  };
  
  return mapping[symbol] || symbol.replace('/', '-');
}
